import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Clock, Ban, Utensils } from 'lucide-react';
import HygieneBadge from './HygieneBadge';

const DealCard = ({ offer, index = 0 }) => {
  const navigate = useNavigate();
  const defaultImages = [
    'https://images.pexels.com/photos/6205791/pexels-photo-6205791.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/33254643/pexels-photo-33254643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    'https://images.pexels.com/photos/36605410/pexels-photo-36605410.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  ];

  const noDeal = !!offer.no_deal;
  const CATEGORY_LABELS = { restaurant: 'Restaurant', cafe: 'Café', takeaway: 'Takeaway', pub: 'Pub', bar: 'Bar', other: 'Venue' };
  const categoryLabel = CATEGORY_LABELS[offer.venue_category] || 'Restaurant';
  const cuisine = offer.cuisine || '';
  const imageUrl = offer.image_url || offer.restaurant_image || defaultImages[index % defaultImages.length];
  const days = offer.days || [];
  const dayLabels = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
    friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
  };

  const target = noDeal ? `/restaurant/${offer.restaurant_id}` : `/deals/${offer.id}`;
  const testId = noDeal ? `restaurant-card-${offer.restaurant_id}` : `deal-card-${offer.id}`;

  const handleClaim = (e) => {
    e.stopPropagation();
    navigate(`/register?role=restaurant&claim=${offer.restaurant_id}`);
  };

  return (
    <div
      onClick={() => navigate(target)}
      className="deal-card bg-white rounded-xl overflow-hidden flex flex-col h-full opacity-0 animate-fade-in-up cursor-pointer"
      style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
      data-testid={testId}
    >
      {/* Image */}
      <div className="relative w-full h-40 bg-[#F5F5F5]">
        <img
          src={imageUrl}
          alt={offer.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImages[0]; }}
        />
        {noDeal ? (
          <div className="absolute top-3 left-3 bg-[#595959] text-white px-3 py-1 rounded-md text-xs font-bold flex items-center gap-1" data-testid="no-deals-pill">
            <Ban className="h-3.5 w-3.5" />
            No Deals
          </div>
        ) : (
          <div className="absolute top-3 left-3 bg-[#7B2CBF] text-white px-3 py-1 rounded-md text-sm font-bold">
            {offer.discount_text}
          </div>
        )}
        {offer.avg_rating > 0 && (
          <div className="absolute top-3 right-3 bg-white text-[#242E30] flex items-center gap-1 px-2 py-1 rounded-md text-sm font-semibold shadow-sm">
            <Star className="h-3.5 w-3.5 fill-[#FFB800] text-[#FFB800]" />
            {offer.avg_rating.toFixed(1)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-[#242E30] text-base mb-1 line-clamp-1">
          {offer.title}
        </h3>

        {/* Cuisine / venue type - knife & fork */}
        <p className="text-sm text-[#595959] mb-1 flex items-center gap-1.5">
          <Utensils className="h-3.5 w-3.5 text-[#7B2CBF] shrink-0" />
          {noDeal
            ? (cuisine ? `${cuisine} • ${categoryLabel}` : categoryLabel)
            : (offer.restaurant_name + (cuisine ? ` • ${cuisine}` : ''))}
        </p>

        {/* Location - pin */}
        {(offer.town_name || offer.postcode_outward) && (
          <p className="text-sm text-[#595959] mb-2 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-[#7B2CBF] shrink-0" />
            {[offer.town_name, offer.postcode_outward].filter(Boolean).join(', ')}
          </p>
        )}

        {!noDeal && offer.description && (
          <p className="text-sm text-[#595959] mb-3 line-clamp-2 flex-1">
            {offer.description}
          </p>
        )}
        {noDeal && <div className="flex-1" />}

        {offer.hygiene_rating ? (
          <div className="mb-3">
            <HygieneBadge rating={offer.hygiene_rating} />
          </div>
        ) : null}

        {noDeal ? (
          offer.is_claimable !== false && (
            <button
              onClick={handleClaim}
              className="text-xs text-[#7B2CBF] font-semibold hover:underline self-start"
              data-testid={`claim-business-${offer.restaurant_id}`}
            >
              Claim this business
            </button>
          )
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Clock className="h-3.5 w-3.5 text-[#595959]" />
            {days.map((day) => (
              <span key={day} className="bg-[#F5F5F5] text-[#242E30] font-medium px-2 py-0.5 rounded text-xs">
                {dayLabels[day] || day}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealCard;
